/**
 * Workflow utility functions for managing and manipulating ComfyUI workflows
 */

/**
 * Randomize seeds and text prompts in a workflow
 * @param {Object} workflow - The workflow object to randomize
 * @returns {Object} - The randomized workflow
 */
export const randomizeWorkflow = (workflow) => {
  if (!workflow) {
    return workflow;
  }

  const randomizedWorkflow = JSON.parse(JSON.stringify(workflow));

  Object.entries(randomizedWorkflow).forEach(([nodeId, node]) => {
    if (node.inputs) {
      // Randomize any parameter containing "seed" (case-insensitive)
      Object.keys(node.inputs).forEach(key => {
        if (key.toLowerCase().includes('seed') && typeof node.inputs[key] === 'number') {
          node.inputs[key] = Math.floor(Math.random() * 1000000000);
        }
      });

      // Randomize text prompts
      if (node.inputs.text && typeof node.inputs.text === 'string') {
        // Add some randomization to text prompts
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        node.inputs.text = `${node.inputs.text} ${randomSuffix}`;
      }
    }
  });

  return randomizedWorkflow;
};

/**
 * Find output nodes in a workflow
 * @param {Object} workflow - The workflow object
 * @returns {Array} - Array of output node IDs
 */
export const findOutputNodes = (workflow) => {
  if (!workflow) {
    return [];
  }

  return Object.entries(workflow)
    .filter(([nodeId, node]) => {
      const classType = node.class_type?.toLowerCase() || '';
      return classType.includes('save') || 
             classType.includes('output') || 
             classType.includes('preview') ||
             classType.includes('display');
    })
    .map(([nodeId]) => nodeId);
};

/**
 * Find input nodes in a workflow
 * @param {Object} workflow - The workflow object
 * @returns {Array} - Array of input node IDs
 */
export const findInputNodes = (workflow) => {
  if (!workflow) {
    return [];
  }

  return Object.entries(workflow)
    .filter(([nodeId, node]) => {
      const classType = node.class_type?.toLowerCase() || '';
      return classType.includes('load') || 
             classType.includes('input') ||
             classType.includes('upload');
    })
    .map(([nodeId]) => nodeId);
};

/**
 * Get all parameters from a workflow
 * @param {Object} workflow - The workflow object
 * @returns {Object} - Object with nodeId -> parameters mapping
 */
export const getAllParameters = (workflow) => {
  if (!workflow) {
    return {};
  }

  const parameters = {};
  
  Object.entries(workflow).forEach(([nodeId, node]) => {
    if (node.inputs) {
      parameters[nodeId] = {
        nodeId,
        classType: node.class_type,
        parameters: Object.entries(node.inputs).map(([key, value]) => ({
          name: key,
          value,
          type: typeof value
        }))
      };
    }
  });

  return parameters;
};

/**
 * Update a specific parameter in a workflow
 * @param {Object} workflow - The workflow object
 * @param {string} nodeId - The node ID
 * @param {string} parameterName - The parameter name
 * @param {any} newValue - The new value
 * @returns {Object} - The updated workflow
 */
export const updateWorkflowParameter = (workflow, nodeId, parameterName, newValue) => {
  if (!workflow || !workflow[nodeId] || !workflow[nodeId].inputs) {
    return workflow;
  }

  const updatedWorkflow = JSON.parse(JSON.stringify(workflow));
  updatedWorkflow[nodeId].inputs[parameterName] = newValue;
  
  return updatedWorkflow;
};

/**
 * Validate a workflow structure
 * @param {Object} workflow - The workflow object
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateWorkflow = (workflow) => {
  const errors = [];
  
  if (!workflow) {
    errors.push('Workflow is null or undefined');
    return { isValid: false, errors };
  }

  if (typeof workflow !== 'object') {
    errors.push('Workflow must be an object');
    return { isValid: false, errors };
  }

  const nodeIds = Object.keys(workflow);
  if (nodeIds.length === 0) {
    errors.push('Workflow contains no nodes');
    return { isValid: false, errors };
  }

  nodeIds.forEach(nodeId => {
    const node = workflow[nodeId];
    if (!node.class_type) {
      errors.push(`Node ${nodeId} missing class_type`);
    }
    if (!node.inputs) {
      errors.push(`Node ${nodeId} missing inputs`);
    }
  });

  return { isValid: errors.length === 0, errors };
};

/**
 * Get workflow statistics
 * @param {Object} workflow - The workflow object
 * @returns {Object} - Workflow statistics
 */
export const getWorkflowStats = (workflow) => {
  if (!workflow) {
    return { nodeCount: 0, outputNodes: 0, inputNodes: 0 };
  }

  const nodeIds = Object.keys(workflow);
  const outputNodes = findOutputNodes(workflow).length;
  const inputNodes = findInputNodes(workflow).length;

  return {
    nodeCount: nodeIds.length,
    outputNodes,
    inputNodes,
    otherNodes: nodeIds.length - outputNodes - inputNodes
  };
};
